<template>
  <div class="location-selector text-sm">
    <AccessTree
      v-model="selection"
      :dealerships="dealerships"
      :scopable-org-ids="[]"
      allow-select-all
    />

    <div
      v-if="!dealerships.length"
      class="text-secondary mt-4"
    >
      {{ $t('ui.support.assistance') }}
    </div>

    <div class="flex flex-wrap items-center justify-between gap-4 mt-4">
      <div class="text-tertiary">
        {{ selection.dealerships.length }} of {{ dealerships.length }}
        {{ $t('ui.entities.dealership', dealerships.length).toLowerCase() }} selected
      </div>
      <Button
        :label="$t('ui.actions.save')"
        :disabled="!selection.dealerships.length"
        @click="handleSave"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
// components
import AccessTree from '@/components/unique/access/AccessTree.vue'
import Button from '@/components/button/Button.vue'
// stores
import { useAccessStore } from '@/stores/AccessStore'
// types
import type { AccessDealership } from '@/utilities/access/accessGroupings'
import type { AccessTreeSelection } from '@/utilities/access/accessTree'

const emit = defineEmits<{ save: [] }>()

const accessStore = useAccessStore()
const { accessAllowed, accessSelected } = storeToRefs(accessStore)

/** The sites this user is allowed to work in */
const dealerships = computed(() => accessAllowed.value.dealerships as unknown as AccessDealership[])

/**
 * Working copy of the picked sites. Orgs are never stored as a scope here: this
 * is a view filter, so ticking an org simply picks the sites beneath it.
 */
const selection = ref<AccessTreeSelection>({ orgs: [], dealerships: [...accessSelected.value.dealerships] })

// Reset the working copy whenever the saved selection changes underneath us
watch(
  () => accessSelected.value.dealerships,
  saved => {
    // Skip echoes of a selection we just saved ourselves
    if (JSON.stringify([...saved].sort()) === JSON.stringify([...selection.value.dealerships].sort())) {
      return
    }
    selection.value = { orgs: [], dealerships: [...saved] }
  }
)

/** Commits the picked sites and lets the host close the selector */
function handleSave(): void {
  accessStore.setSelectedDealerships(selection.value.dealerships)
  emit('save')
}
</script>

<style scoped>
.location-selector {
  inline-size: 100%;
  margin-inline: auto;
  max-inline-size: 640px;
}
</style>
